<script setup lang="ts">
import {UserCircle, Users03, LogOut04} from "utruckin-icons";
import {onBeforeUnmount, onMounted, ref} from "vue";
import {removeToken} from "@/utils/token";
import {useRouter} from "vue-router";

const router = useRouter();

const isDropdownVisible = ref<boolean>(false);
const profileRef = ref<HTMLElement | null>(null)

const toggleDropdown = () => {
  isDropdownVisible.value = !isDropdownVisible.value;
}

const closeDropdown = () => {
  isDropdownVisible.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (
      profileRef.value &&
      !profileRef.value.contains(event.target as Node)
  ) {
    closeDropdown()
  }
}

const handleEsc = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeDropdown()
  }
}

const signOut = () => {
  removeToken()
  router.push("/")
}

const goToSettings = (type: 'profile' | 'users') => {
  router.push({
    path: "/settings",
    query: {
      name: type
    }
  })

  closeDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEsc)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEsc)
})
</script>

<template>
  <div class="w-profile" ref="profileRef">
    <div class="body" @click="toggleDropdown">
      <div class="body__icon">
        SA
      </div>

      <div class="body__title">
        Super admin
      </div>
    </div>

    <div class="dropdown" v-if="isDropdownVisible">
      <div class="dropdown__item" @click="goToSettings('profile')">
        <div class="dropdown__item--icon">
          <UserCircle :size="16"/>
        </div>
        <div class="dropdown__item--title">
          Profile
        </div>
      </div>
      <div class="dropdown__item" @click="goToSettings('users')">
        <div class="dropdown__item--icon">
          <Users03 :size="16"/>
        </div>
        <div class="dropdown__item--title">
          Users
        </div>
      </div>
      <div class="dropdown__item" @click="signOut">
        <div class="dropdown__item--icon">
          <LogOut04 :size="16"/>
        </div>
        <div class="dropdown__item--title">
          Sign out
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.w-profile {
  position: relative;
  cursor: pointer;

  .body {
    padding: 4px 12px 4px 4px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;

    &__icon {
      width: 32px;
      height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--color-primary);
      color: var(--color-white);
      border-radius: 10px;
    }

    &__title {
      color: var(--color-label);
    }

    &:hover {
      border-color: var(--color-secondary);
    }
  }

  .dropdown {
    position: absolute;
    border-radius: 14px;
    border: 1px solid var(--color-border);
    background: var(--color-white);
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    overflow: hidden;
    z-index: 6;

    &__item {
      padding: 12px;
      display: flex;
      align-items: center;
      gap: 12px;

      &--icon {
        width: 16px;
        height: 16px;
        background: transparent;
      }

      &--title {
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
        background: transparent;
      }

      &:hover {
        background: var(--color-bg-secondary);
      }

      &:first-child {
        padding: 14px 12px 10px 12px;
        color: var(--color-label);
      }

      &:nth-child(2) {
        padding: 10px 12px;
        color: var(--color-label);
      }

      &:last-child {
        border-top: 1px solid var(--color-border);
        color: var(--color-red);
      }
    }
  }
}
</style>